// 📁 src/utils/audioRecorder.js
// 🎤 Audio Recorder - Graba audio del micrófono usando MediaRecorder API

/**
 * Clase para manejar la grabación de audio del micrófono
 * Responsable de:
 * - Solicitar permisos de micrófono
 * - Iniciar/detener grabación
 * - Devolver Blob de audio con metadata
 */
class AudioRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.startTime = null;
  }

  /**
   * Verifica si ya se tienen permisos de micrófono (sin solicitar popup)
   * P0-VOICE-UX: Separar check de request
   * @returns {Promise<boolean>} true si ya hay permisos concedidos
   */
  async checkPermissions() {
    try {
      // Usar Permissions API si está disponible (Chrome, Edge)
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        return result.state === 'granted';
      }
      
      // Fallback: intentar obtener stream brevemente
      // (Safari no tiene Permissions API)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch {
        return false;
      }
    } catch (error) {
      console.warn('[AudioRecorder] No se pudo verificar permisos:', error);
      return false;
    }
  }

  /**
   * Solicita permisos de micrófono y verifica disponibilidad
   * @returns {Promise<boolean>} true si se obtuvieron permisos
   */
  async requestPermissions() {
    try {
      // Verificar que el navegador soporte getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Tu navegador no soporta grabación de audio');
      }

      // Solicitar permisos
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000 // OpenAI recomienda 16kHz
        } 
      });
      
      // Cerrar stream temporal (solo para verificar permisos)
      stream.getTracks().forEach(track => track.stop());
      
      return true;
    } catch (error) {
      console.error('[AudioRecorder] Error al solicitar permisos:', error);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        throw new Error('Permiso de micrófono denegado. Por favor, habilita el micrófono en la configuración de tu navegador.');
      } else if (error.name === 'NotFoundError') {
        throw new Error('No se encontró ningún micrófono. Por favor, conecta un micrófono e intenta de nuevo.');
      } else {
        throw new Error(`Error al acceder al micrófono: ${error.message}`);
      }
    }
  }

  /**
   * Inicia la grabación de audio
   * @returns {Promise<void>}
   */
  async start() {
    try {
      // Obtener stream de audio
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });

      // Determinar tipo MIME soportado
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }

      // Crear MediaRecorder
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
      this.audioChunks = [];
      this.startTime = Date.now();

      // Capturar chunks de audio
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      // Iniciar grabación
      this.mediaRecorder.start();
      
      console.log('[AudioRecorder] Grabación iniciada con formato:', mimeType);
    } catch (error) {
      console.error('[AudioRecorder] Error al iniciar grabación:', error);
      this.cleanup();
      throw error;
    }
  }

  /**
   * 🎟️ TICKET-3: Inicia grabación con analizador de señal (RMS/peak detection)
   * @returns {Promise<{stream: MediaStream, analyser: AnalyserNode}>}
   */
  async startWithAnalyzer() {
    try {
      // Obtener stream de audio
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });

      // 🎟️ TICKET-3: Crear AudioContext + AnalyserNode para análisis de señal
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(this.stream);
      const analyser = audioContext.createAnalyser();
      
      // Configuración del analizador
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      
      // Conectar source → analyser (sin salida de audio)
      source.connect(analyser);
      
      console.log('[AudioRecorder] AnalyserNode creado - FFT size:', analyser.fftSize);

      // Determinar tipo MIME soportado
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }

      // Crear MediaRecorder
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
      this.audioChunks = [];
      this.startTime = Date.now();

      // Capturar chunks de audio
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      // Iniciar grabación
      this.mediaRecorder.start();
      
      console.log('[AudioRecorder] Grabación iniciada con formato:', mimeType);
      
      return { stream: this.stream, analyser };
    } catch (error) {
      console.error('[AudioRecorder] Error al iniciar grabación con analyzer:', error);
      this.cleanup();
      throw error;
    }
  }

  /**
   * Detiene la grabación y devuelve el audio
   * @returns {Promise<{blob: Blob, mimeType: string, durationMs: number}>}
   */
  async stop() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('No hay grabación activa'));
        return;
      }

      // Manejar el evento de parada
      this.mediaRecorder.onstop = () => {
        const duration = Date.now() - this.startTime;
        const mimeType = this.mediaRecorder.mimeType;
        
        // Crear blob con los chunks
        const blob = new Blob(this.audioChunks, { type: mimeType });
        
        console.log('[AudioRecorder] Grabación detenida:', {
          size: blob.size,
          type: mimeType,
          duration: `${duration}ms`
        });

        // Limpiar recursos
        this.cleanup();

        resolve({
          blob,
          mimeType,
          durationMs: duration
        });
      };

      // Manejar errores
      this.mediaRecorder.onerror = (error) => {
        console.error('[AudioRecorder] Error durante grabación:', error);
        this.cleanup();
        reject(error);
      };

      // Detener grabación
      this.mediaRecorder.stop();
    });
  }

  /**
   * Cancela la grabación actual sin devolver audio
   */
  cancel() {
    console.log('[AudioRecorder] Grabación cancelada');
    
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    
    this.cleanup();
  }

  /**
   * Verifica si hay una grabación activa
   * @returns {boolean}
   */
  isRecording() {
    return this.mediaRecorder && this.mediaRecorder.state === 'recording';
  }

  /**
   * Limpia recursos (stream, chunks)
   * @private
   */
  cleanup() {
    // Detener tracks del stream
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    // Limpiar chunks
    this.audioChunks = [];
    this.mediaRecorder = null;
    this.startTime = null;
  }
}

// Exportar instancia singleton
export const audioRecorder = new AudioRecorder();

export default AudioRecorder;
